import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  icon,
  children,
  disabled,
  className = '',
  ...props
}) => {
  const { theme } = useTheme();

  // Size-based styles (ensure minimum 44px height for touch targets)
  const sizeClasses = {
    sm: 'px-4 py-2.5 text-sm min-h-[44px]', // min 44px for touch
    md: 'px-6 py-3 text-base min-h-[48px]',  // comfortable 48px
    lg: 'px-8 py-4 text-lg min-h-[56px]',    // large 56px
  };

  // Variant-based styles using theme colors
  const getVariantStyle = () => {
    const baseStyle = {
      transition: 'all 0.2s ease',
    };

    switch (variant) {
      case 'primary':
        return {
          ...baseStyle,
          background: theme.colors.primary,
          color: theme.colors.onPrimary,
          border: 'none',
        };
      case 'secondary':
        return {
          ...baseStyle,
          background: theme.colors.secondary,
          color: theme.colors.onSecondary,
          border: 'none',
        };
      case 'outline':
        return {
          ...baseStyle,
          background: 'transparent',
          color: theme.colors.onSurface,
          border: `2px solid ${theme.colors.outline}`,
        };
      case 'ghost':
        return {
          ...baseStyle,
          background: 'transparent',
          color: theme.colors.onSurface,
          border: 'none',
        };
      default:
        return baseStyle;
    }
  };

  // Base classes for all buttons
  const baseClasses = `
    font-semibold
    rounded-[${theme.borderRadius.md}]
    ${theme.elevation.md}
    ${theme.animations.transition}
    transform
    active:scale-95
    disabled:opacity-50
    disabled:cursor-not-allowed
    disabled:transform-none
    flex
    items-center
    justify-center
    gap-2
  `.trim().replace(/\s+/g, ' ');

  // Hover effects (only when not disabled)
  const hoverClasses = disabled || loading ? '' : theme.animations.buttonHover;

  // Width classes
  const widthClass = fullWidth ? 'w-full' : '';

  return (
    <button
      {...props}
      disabled={disabled || loading}
      className={`${baseClasses} ${sizeClasses[size]} ${hoverClasses} ${widthClass} ${className}`}
      style={getVariantStyle()}
    >
      {loading && (
        <svg
          className="animate-spin h-5 w-5"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      )}
      {!loading && icon && <span>{icon}</span>}
      <span>{children}</span>
    </button>
  );
};
